import React from "react";
import { Box } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { BRAND, LOGO_SRC, LOGO_SRC_SVG } from "./brandTokens";

const LOGO_MESH_LINES = (() => {
  const cx = 50;
  const cy = 50;
  const r = 42;
  const points = Array.from({ length: 10 }, (_, i) => {
    const angle = ((i * 36 - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  });

  const lines = points.map((p) => [cx, cy, p.x, p.y]);
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    lines.push([a.x, a.y, b.x, b.y]);
    const skip = points[(i + 3) % points.length];
    lines.push([a.x, a.y, skip.x, skip.y]);
  }
  return lines;
})();

const DEFAULT_SIZE = { xs: 136, sm: 152, md: 188, lg: 208 };

/**
 * Mesh çizgili parıltı + yüzen logo (footer ve splash).
 * @param {"view"|"immediate"} entrance — scroll ile mi, hemen mi giriş animasyonu
 */
export function AnimatedBrandLogo({
  size = DEFAULT_SIZE,
  idSuffix = "brand",
  entrance = "view",
  alt = "PatientMesh",
  reduceMotion: reduceMotionProp,
  onImageError,
  sx = {},
}) {
  const reduceMotionHook = useReducedMotion();
  const reduceMotion = reduceMotionProp ?? reduceMotionHook;
  const gradientId = `meshSpark-${idSuffix}`;
  const [logoSrc, setLogoSrc] = React.useState(LOGO_SRC);

  const handleLogoError = () => {
    setLogoSrc((prev) => {
      if (prev === LOGO_SRC) return LOGO_SRC_SVG;
      onImageError?.();
      return prev;
    });
  };

  const floatTransition = reduceMotion
    ? undefined
    : { y: { duration: 5, repeat: Infinity, ease: "easeInOut" } };

  const entranceProps =
    entrance === "immediate"
      ? {
          initial: reduceMotion ? false : { opacity: 0, scale: 0.88 },
          animate: reduceMotion
            ? { opacity: 1, scale: 1 }
            : { opacity: 1, scale: 1, y: [0, -6, 0] },
          transition: reduceMotion
            ? { duration: 0.65, ease: [0.22, 1, 0.36, 1] }
            : {
                duration: 0.65,
                ease: [0.22, 1, 0.36, 1],
                ...floatTransition,
              },
        }
      : {
          initial: reduceMotion ? false : { opacity: 0, scale: 0.9 },
          whileInView: { opacity: 1, scale: 1 },
          viewport: { once: true },
          animate: reduceMotion ? undefined : { y: [0, -6, 0] },
          transition: reduceMotion
            ? { duration: 0.65, ease: [0.22, 1, 0.36, 1] }
            : {
                duration: 0.65,
                ease: [0.22, 1, 0.36, 1],
                ...floatTransition,
              },
        };

  return (
    <Box
      sx={{
        position: "relative",
        flexShrink: 0,
        width: size,
        height: size,
        ...sx,
      }}
    >
      {!idSuffix.startsWith("navbar") && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: "-6%",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.tealGlow} 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
      )}

      <Box
        component={motion.div}
        {...entranceProps}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        <Box
          component="img"
          src={logoSrc}
          alt={alt}
          onError={handleLogoError}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            position: "relative",
            zIndex: 1,
            filter: idSuffix.startsWith("navbar")
              ? "none"
              : "drop-shadow(0 14px 40px rgba(29, 184, 160, 0.5))",
          }}
        />

        {!reduceMotion && !idSuffix.startsWith("navbar") && (
          <Box
            component="svg"
            viewBox="0 0 100 100"
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              pointerEvents: "none",
              mixBlendMode: "screen",
            }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={BRAND.teal} stopOpacity="0" />
                <stop offset="45%" stopColor="#7ef0dc" stopOpacity="0.95" />
                <stop offset="55%" stopColor="#ffffff" stopOpacity="1" />
                <stop offset="100%" stopColor={BRAND.teal} stopOpacity="0" />
              </linearGradient>
            </defs>

            {LOGO_MESH_LINES.map(([x1, y1, x2, y2], i) => (
              <g key={`mesh-${idSuffix}-${i}`}>
                <motion.line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={BRAND.teal}
                  strokeWidth="0.35"
                  strokeOpacity={0.22}
                  strokeLinecap="round"
                />
                <motion.line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={`url(#${gradientId})`}
                  strokeWidth="0.55"
                  strokeLinecap="round"
                  strokeDasharray="3 14"
                  animate={{ strokeDashoffset: [0, -34] }}
                  transition={{
                    duration: 1.6 + (i % 5) * 0.22,
                    repeat: Infinity,
                    ease: "linear",
                    delay: (i % 7) * 0.18,
                  }}
                />
              </g>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
